import React, { useMemo, useState } from "react";
import "./AddUser.css";
import { useSelector } from "react-redux";
import { delete_user_role, getUserFullDetailsById } from '../../helper/Api';
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import CustomBreadcrumbs from "../Tools/Breadcumbs";

const normalizeRoleValue = (role) => String(role || "").trim().toLowerCase();

const roleChipStyles = (role) => {
  const r = normalizeRoleValue(role);
  if (r === "l3") {
    return {
      label: "L3",
      sx: {
        bgcolor: "rgba(25, 135, 84, 0.14)",
        color: "#198754",
        fontWeight: 700,
      },
    };
  }
  if (r === "l2") {
    return {
      label: "L2",
      sx: {
        bgcolor: "rgba(36, 144, 234, 0.14)",
        color: "#2490EA",
        fontWeight: 700,
      },
    };
  }
  if (r === "l1" || r === "vo") {
    return {
      label: r === "vo" ? "VO" : "L1",
      sx: {
        bgcolor: "rgba(108, 117, 125, 0.14)",
        color: "#495057",
        fontWeight: 700,
      },
    };
  }
  return {
    label: (role ?? "-").toString(),
    sx: {
      bgcolor: "rgba(108, 117, 125, 0.14)",
      color: "#495057",
      fontWeight: 700,
    },
  };
};

const ViewUser = () => {
    const [showModal, setShowModal] = useState(false);
    const { id } = useParams();
    const { authData } = useSelector((state) => state.authData);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        code: null,
        user_name: null,
        designation: null,
        user_type: null,
        mobile: null,
        email: null,
        password: null,
        added_by: authData.user.id,
        departments: [],
    });

    useEffect(() => {
        get_user_details();
    }, [id])

    const get_user_details = async () => {
        try {
            const { data } = await getUserFullDetailsById(id);
            const user = data.data;
            setFormData({
                code: user.code,
                user_name: user.user_name,
                designation: user.designation,
                user_type: user.user_type,
                mobile: user.mobile,
                email: user.email,
                password: '',
                added_by: user.added_by,
                departments: user.roles
            })
        } catch (error) {
            // console.log(error);
        }
    }
    
    const handleFormDataChange = (e) => {
        const name = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [name]: value })
    }

    const deleteDepartment = async () => {
        try {
            const res = await delete_user_role({ role_id: roleToDelete });
            const message =
                res?.data?.message ||
                res?.data?.msg ||
                "Role deleted successfully.";
            toast.success(message, {
                position: toast.POSITION.TOP_CENTER
            });
            setFormData((prevData) => {
                const updatedDepartments = prevData.departments.filter(department => department.id !== roleToDelete);
                return {
                    ...prevData,
                    departments: updatedDepartments,
                };
            });
            setShowModal(false);
            setRoleToDelete(null);
        }
        catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Error while deleting role.";
            toast.error(message, {
                position: toast.POSITION.TOP_CENTER
            });
        }
    };
    const handleDeleteClick = (roleId) => {
        setShowModal(true);
        setRoleToDelete(roleId);
    };

    const handleCancelDelete = () => {
        setShowModal(false);
        setRoleToDelete(null);
    };

    const linksArray = [
            {
                href: "/users",
                label: "Users",
            }
        ];

    const isL3User = normalizeRoleValue(authData?.user?.user_type) === "l3";
    const deleteRoleName = useMemo(() => {
      const role = formData.departments?.find((dept) => dept.id === roleToDelete);
      if (!role) return null;
      return `${role?.name || "-"} (${role?.department_level || "-"}) · ${role?.code || "-"}`;
    }, [formData.departments, roleToDelete]);

    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            px: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "#28326f", fontWeight: 700, m: 0 }}
            >
              View User Roles
            </Typography>
            <CustomBreadcrumbs linksArray={linksArray} title={"View User Roles"} />
          </Box>

          {isL3User && (
            <Button
              variant="contained"
              onClick={() => navigate("/add-user")}
              sx={{
                textTransform: "none",
                bgcolor: "#28326f",
                "&:hover": { bgcolor: "#1e255a" },
                borderRadius: 2,
              }}
            >
              Add User Role
            </Button>
          )}
        </Box>

        {/* User summary form */}
        <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Employee Code
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formData.code || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                User Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formData.user_name || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Designation
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formData.designation || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Mobile Number
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formData.mobile || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Email ID
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formData.email || "-"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Roles table */}
        <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              Assigned Roles
            </Typography>
          </Box>

          <TableContainer sx={{ borderRadius: 2, border: "1px solid rgba(0,0,0,0.06)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{
                      bgcolor: "#2490EA",
                      color: "white",
                      fontWeight: 700,
                    }}
                  >
                    Department Code
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      bgcolor: "#2490EA",
                      color: "white",
                      fontWeight: 700,
                    }}
                  >
                    Department Name
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: "#2490EA",
                      color: "white",
                      fontWeight: 700,
                      width: 120,
                    }}
                  >
                    Level
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: "#2490EA",
                      color: "white",
                      fontWeight: 700,
                      width: 140,
                    }}
                  >
                    User Role
                  </TableCell>
                  {isL3User && (
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: "#2490EA",
                        color: "white",
                        fontWeight: 700,
                        width: 90,
                      }}
                    >
                      Action
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(formData.departments || []).map((dept, idx) => {
                  const chip = roleChipStyles(dept.user_type);
                  return (
                    <TableRow
                      key={dept.id}
                      sx={{
                        bgcolor: idx % 2 === 0 ? "rgba(0,0,0,0.015)" : "white",
                        "&:hover": { bgcolor: "rgba(36, 144, 234, 0.06)" },
                      }}
                    >
                      <TableCell align="left" sx={{ fontWeight: 600 }}>
                        {dept.code || "-"}
                      </TableCell>
                      <TableCell align="left">{dept.name || "-"}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={dept.department_level || "-"}
                          sx={{
                            bgcolor: "rgba(13, 110, 253, 0.10)",
                            color: "#0d6efd",
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={chip.label} sx={chip.sx} />
                      </TableCell>
                      {isL3User && (
                        <TableCell align="center">
                          <Tooltip title="Delete role" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(dept.id)}
                              sx={{
                                color: "#dc3545",
                                bgcolor: "rgba(220,53,69,0.08)",
                                "&:hover": { bgcolor: "rgba(220,53,69,0.14)" },
                              }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}

                {!formData.departments?.length && (
                  <TableRow>
                    <TableCell colSpan={isL3User ? 5 : 4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No roles found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={showModal} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              Are you sure you want to delete this role?
            </DialogContentText>
            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: "rgba(0,0,0,0.03)",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {deleteRoleName || `Role ID: ${roleToDelete ?? "-"}`}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCancelDelete} variant="outlined" sx={{ textTransform: "none", borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              onClick={deleteDepartment}
              variant="contained"
              color="error"
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
}
export default ViewUser
